import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  generateNonce,
  loadGoogleIdentityServices,
  type GoogleCredentialResponse,
} from "./google-native";

interface GoogleNativeButtonProps {
  clientId: string;
  onCredential: (idToken: string, rawNonce: string) => void;
  fallback: React.ReactNode;
}

type Status = "loading" | "ready" | "unavailable";

const TARGET_HEIGHT_PX = 48;
const MAX_UPSCALE = 1.1;

export function GoogleNativeButton({ clientId, onCredential, fallback }: GoogleNativeButtonProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onCredentialRef = useRef(onCredential);
  const rawNonceRef = useRef("");
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    void (async () => {
      try {
        const nonce = await generateNonce();
        if (cancelled) return;
        rawNonceRef.current = nonce.raw;

        const gis = await loadGoogleIdentityServices(6000);
        if (cancelled) return;

        gis.initialize({
          client_id: clientId,
          nonce: nonce.hashed,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: true,
          callback: (response: GoogleCredentialResponse) => {
            if (response?.credential) {
              onCredentialRef.current(response.credential, rawNonceRef.current);
            }
          },
        });

        await new Promise(requestAnimationFrame);
        if (cancelled) return;

        container.replaceChildren();
        const measured = Math.round(container.getBoundingClientRect().width);
        const targetWidth = Math.min(measured > 0 ? measured : 320, 400);
        const buttonOptions = {
          type: "standard",
          theme: "filled_black",
          size: "large",
          text: "continue_with",
          shape: "pill",
          logo_alignment: "left",
          locale: "pt-BR",
        } as const;

        gis.renderButton(container, { ...buttonOptions, width: targetWidth });

        const firstPass = container.firstElementChild as HTMLElement | null;
        const nativeHeight = firstPass?.getBoundingClientRect().height ?? 0;
        const isPersonalizedCard = !!firstPass?.querySelector("img");
        if (
          firstPass &&
          !isPersonalizedCard &&
          nativeHeight > 0 &&
          Math.round(nativeHeight) !== TARGET_HEIGHT_PX
        ) {
          const targetScale = TARGET_HEIGHT_PX / nativeHeight;
          const scale = targetScale > 1 ? Math.min(targetScale, MAX_UPSCALE) : targetScale;
          container.replaceChildren();
          gis.renderButton(container, {
            ...buttonOptions,
            width: Math.round(targetWidth / scale),
          });
          const scaled = container.firstElementChild as HTMLElement | null;
          if (scaled) {
            scaled.style.transform = `scale(${scale})`;
            scaled.style.transformOrigin = "center";
          }
        }

        if (!cancelled) setStatus("ready");
      } catch {
        if (!cancelled) setStatus("unavailable");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (status === "unavailable") {
    return <>{fallback}</>;
  }

  return (
    <div className="relative">
      <div ref={containerRef} className="flex h-12 items-center justify-center" />
      {status === "loading" && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
        </div>
      )}
    </div>
  );
}
